import type { RecognizedScreen } from '../api/insights-screen';
import type { PresentationMedia } from '../api/presentation-media';
import type { ScreenImage } from './render/insights-canvas';

/**
 * Строки табличных отчётов по публикациям — акта об услугах и «посева».
 *
 * Всё, кроме экрана, — строки, а не числа: таблицу правят руками прямо на
 * странице, и наполовину набранная ячейка («1 7», пока дописывают) не
 * должна ломать модель. Итоги считаются из того, что в ячейках читается
 * как число; остальное остаётся текстом и уезжает в Word как есть.
 */

export type PostRow = {
  /** Свой ключ: строки переставляют и удаляют, индекс для React не годится. */
  key: string;
  resource: string;
  /** ДД.ММ.ГГГГ. */
  date: string;
  url: string;
  format: string;
  views: string;
  reach: string;
  cost: string;
  /** Экран статистики: нарисован по цифрам или принесён человеком. */
  screen: ScreenImage | null;
  /** Шорткод публикации; null — строка заведена руками. */
  shortcode: string | null;
  /** Цифр от InstaFarm нет: строку заполняет человек. */
  manual: boolean;
};

const MONTHS_GENITIVE = [
  'января', 'февраля', 'марта', 'апреля', 'мая', 'июня',
  'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря',
];

/** Формат публикации так, как его называет договор, а не Graph API. */
export function formatOf(media: Pick<PresentationMedia, 'mediaType' | 'productType'>): string {
  if (media.productType === 'REELS') return 'Reels';
  if (media.mediaType === 'CAROUSEL_ALBUM') return 'Карусель';
  if (media.mediaType === 'VIDEO') return 'Видео';
  return 'Пост';
}

/** «2026-09-14T11:57:32+0000» → «14.09.2026». */
export function shortDate(iso: string | null): string {
  if (!iso) return '';
  const [year, month, day] = iso.slice(0, 10).split('-');
  return `${day}.${month}.${year}`;
}

/** «14.09.2026» → ««14» сентября 2026 года» — как в шапке отчёта. */
export function longDate(short: string): string {
  const [day, month, year] = short.split('.');
  const index = Number(month) - 1;
  if (!day || !year || !MONTHS_GENITIVE[index]) return short;
  return `«${day}» ${MONTHS_GENITIVE[index]} ${year} года`;
}

/** Число с пробелами между разрядами: «356 289». */
export function spacedNumber(value: number): string {
  return String(Math.round(value)).replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
}

/** Число из ячейки: пробелы внутри цифр не мешают, буквы — мешают. */
export function cellNumber(text: string): number | null {
  const digits = text.replace(/[\s ]/g, '');
  if (!/^\d+$/.test(digits)) return null;
  return Number(digits);
}

export function emptyRow(url = ''): PostRow {
  return {
    key: crypto.randomUUID(),
    resource: '',
    date: '',
    url,
    format: '',
    views: '',
    reach: '',
    cost: '',
    screen: null,
    shortcode: null,
    manual: true,
  };
}

/**
 * Строки по списку ссылок. Порядок — как в списке: его задаёт человек, и в
 * отчёте он тот же, что в переписке с клиентом.
 *
 * По ссылке, которой нет в ответе InstaFarm, строка всё равно появляется:
 * публикация была, её просто некому посчитать — аккаунт не подключён.
 * Пустые ячейки заполняют руками; это честнее, чем молча выбросить
 * размещение из акта.
 */
export function buildRows(
  links: Array<{ url: string; shortcode: string | null }>,
  media: PresentationMedia[],
): PostRow[] {
  const byShortcode = new Map(media.map((m) => [m.shortcode, m]));
  return links.map(({ url, shortcode }) => {
    const found = shortcode ? byShortcode.get(shortcode) : undefined;
    if (!found) return { ...emptyRow(url), shortcode };
    return {
      key: crypto.randomUUID(),
      resource: `@${found.username}`,
      date: shortDate(found.publishedAt),
      url,
      format: formatOf(found),
      views: found.views === null ? '' : spacedNumber(found.views),
      reach: found.reach === null ? '' : spacedNumber(found.reach),
      cost: '',
      screen: null,
      shortcode: found.shortcode,
      manual: false,
    };
  });
}

export type PostTotals = { views: number; reach: number; cost: number };

/** Итоги — сумма того, что в ячейках читается как число. */
export function totalsOf(rows: PostRow[]): PostTotals {
  const sum = (pick: (row: PostRow) => string) =>
    rows.reduce((acc, row) => acc + (cellNumber(pick(row)) ?? 0), 0);
  return { views: sum((r) => r.views), reach: sum((r) => r.reach), cost: sum((r) => r.cost) };
}

/** Период по датам размещений: его правят руками, но начинать с пустого незачем. */
export function periodOf(rows: PostRow[]): { from: string; to: string } {
  const key = (date: string) => date.split('.').reverse().join('-');
  const dates = rows
    .map((row) => row.date)
    .filter((date) => /^\d{2}\.\d{2}\.\d{4}$/.test(date))
    .sort((a, b) => key(a).localeCompare(key(b)));
  return { from: dates[0] ?? '', to: dates[dates.length - 1] ?? '' };
}

/**
 * Цифры со скриншота → правка строки таблицы.
 *
 * Заполняем только пустое: набранное руками важнее распознанного, а модель
 * ошибается на размытых снимках. Поэтому это «подставить недостающее», а не
 * «переписать строку», и человек всё равно смотрит результат.
 */
export function patchFromScreen(row: PostRow, data: RecognizedScreen): Partial<PostRow> {
  const patch: Partial<PostRow> = {};
  const fill = (field: 'resource' | 'format' | 'views' | 'reach', value: string | null): void => {
    if (!value) return;
    if (row[field].trim()) return;
    patch[field] = value;
  };

  fill('resource', data.username);
  fill('format', data.kind === 'reel' ? 'Reels' : 'Пост');
  fill('views', data.views === null ? null : String(data.views));
  fill('reach', data.reach === null ? null : String(data.reach));
  return patch;
}
